import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { logImpression } from '@office-start/telemetry-service';
import { KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ClassNames } from '../shared/icon-font/icon-font-classes.utility';
import { IconFontType } from '../shared/icon-font/icon-font-type.enum';
import { InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { EventMetric } from '../shared/instrumentation/user-profile.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { EventService } from '../shared/services/event.service';
import { WebPwaService } from '../shared/services/web-pwa.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AnnouncementType } from './announcement-type.enum';
import { AnnouncementModel } from './announcement.model';
import { AnnouncementService } from './announcement.service';
import { BannerConfigService } from './banner-config.service';

import './banner.component.css';

@Component({
    selector: 'ohp-banner',
    templateUrl: './banner.component.html'
})
export class BannerComponent implements OnInit, OnDestroy {

    @Input('bottomAligned') public bottomAligned: boolean = false;
    @Input() public viewMetric: EventMetric;
    @Input('bottomTrayEnabled') public bottomTrayEnabled: boolean = false;

    public announcements: Array<AnnouncementModel> = [];
    public desktopBottomAligned: boolean;
    private currentAnnouncementIndex = -1;
    private maxAnnouncements: number;

    private announcementEventSubscription: Subscription;

    constructor(
        private readonly announcementService: AnnouncementService,
        private readonly bannerConfigService: BannerConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly deviceDetector: DeviceDetector,
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly webPwaService: WebPwaService
    ) {
    }

    public async ngOnInit() {
        this.maxAnnouncements = this.bottomAligned ? 1 : 2;
        this.desktopBottomAligned = !this.deviceDetector.isMobileDevice() && this.bottomAligned;

        const currentAnnouncements = this.announcementService.getAnnouncements() || [];
        const filteredAnnouncements = currentAnnouncements.filter(
            announcement => announcement.isValid() && announcement.alignBottom === this.bottomAligned
        ).reduce((newAnnouncements, announcement) => {
            if (newAnnouncements.length < this.maxAnnouncements) {
                newAnnouncements.push(announcement);
            }
            return newAnnouncements;
        }, []);

        this.announcements = filteredAnnouncements;
        this.eventService.broadcastEvent({
            name: AngularEvents.announcementsReady,
            args: [this.announcements.length]
        });

        // Setting ObservableScope to future only makes us miss the previous events on pageload since banner.module is being lazy loaded.
        this.announcementEventSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.newAnnouncement || event.name === AngularEvents.closeAnnouncement))
            .subscribe(event => {
                if (!event.args.length) {
                    return;
                }

                if (event.name === AngularEvents.closeAnnouncement) {
                    for (let i = 0; i < this.announcements.length; i++) {
                        const currentAnnouncement = this.announcements[i];
                        if (currentAnnouncement.announcementType === event.args[0] && (event.args.length === 1 || currentAnnouncement.index === event.args[1])) {
                            this.announcements.splice(i, 1);
                            return;
                        }
                    }
                    return;
                }

                const announcement: AnnouncementModel = event.args[0];

                if (!announcement || !announcement.isValid() || announcement.alignBottom !== this.bottomAligned) {
                    return;
                }

                this.currentAnnouncementIndex += 1;
                announcement.index = this.currentAnnouncementIndex;

                const result = this.announcements.slice(0).filter(curr => curr.announcementType !== announcement.announcementType);
                result.push(announcement);

                this.announcements = result.sort((a, b) => b.importance - a.importance).slice(0, this.maxAnnouncements);
            });

        this.announcementService.setUpSessionExpiredAnnouncement();

        if (this.capabilitiesService.capabilities.appManifestEnabled && this.viewMetric) {
            // TODO: Add screenshot test for a2hs prompt
            this.announcementService.setUpAddToHomeScreenPrompt(this.viewMetric)
                .then(showPrompt => {
                    // Listen for before install prompt
                    if (showPrompt) {
                        this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                            filter(event => event.name === AngularEvents.beforeInstallPrompt),
                            take(1)
                        ).toPromise().then(event => {
                            const resources = this.bannerConfigService.config.resources;
                            const isMobile = this.deviceDetector.isMobileDevice();

                            const iconClass = isMobile ? undefined : `${ClassNames.FluentIconCssClass} ${ClassNames.getCssClass(IconFontType.PinDown)} announcement-section__icon--a2hs`;

                            const announcement = new AnnouncementModel(
                                AnnouncementType.AddToHomeScreen,
                                isMobile ? resources.A2HSPromptTitle : resources.A2HSPromptTitleDesktop,
                                null,
                                iconClass,
                                null,
                                isMobile,
                                'ms-bgc-w',
                                'ms-fcl-tp',
                                this.webPwaService.isStandalone,
                                true
                            );

                            announcement.primaryButton = {
                                id: 'add-to-homescreen-btn',
                                text: isMobile ? resources.AddButtonText : resources.AddButtonDesktopText,
                                ariaLabel: isMobile ? resources.A2HSButtonAriaLabel : resources.A2HSButtonAriaLabel,
                                dismissOnClick: true,
                                onClick: () => event.args[0].prompt()
                            };

                            this.eventService.broadcastEvent({
                                name: AngularEvents.newAnnouncement,
                                args: [announcement]
                            });

                            const addToHomeScreenPromptImpression = isMobile ? KnownInstrumentationEvents.AddToHomeScreenPromptImpressionMobile : KnownInstrumentationEvents.AddToHomeScreenPromptImpressionDesktop;
                            const addToHomeScreenPromptOtelImpression = isMobile ? KnownImpressionInstrumentationEvents.AddToHomeScreenPromptImpressionMobile : KnownImpressionInstrumentationEvents.AddToHomeScreenPromptImpressionDesktop;

                            this.instrumentationService.logImpressionEvent(addToHomeScreenPromptImpression, {}, [addToHomeScreenPromptImpression]);
                            logImpression({ eventName: addToHomeScreenPromptOtelImpression, area: 'Banner' });
                        });
                    }
                });
        }
    }

    public ngOnDestroy() {
        if (this.announcementEventSubscription) {
            this.announcementEventSubscription.unsubscribe();
        }
    }

    public trackByIndex(_: number, obj: AnnouncementModel) {
        return obj.index;
    }
}
